/**
 * Styled button-trigger dropdown that matches the visual language of
 * `PaymentTypeDropdown` and `TrackingDropdown` on the Spend / Receive Money
 * form (#6891). Used in place of native HTML `<select>` elements that
 * inherit OS-level styling (blue macOS highlight, system font) and break
 * design parity with the rest of the form.
 *
 * Renders a portal-positioned menu so the panel is not clipped by overflow
 * containers (e.g. the line items table cells).
 */

import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
  type ForwardedRef,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/cn';
import { contiguousRuns } from '../../lib/contiguous_runs';
import { useFormFieldContext } from '../form_field/form_field';

export interface StyledSelectOption<V extends string = string> {
  value: V;
  label: string;
  /**
   * Renders the option `aria-disabled="true"` and excludes it from the
   * active-index, ArrowUp/ArrowDown/Home/End, and typeahead handling below —
   * it can never become the initial active index, and a click or Enter/Space
   * on it is a no-op (W3-b, #24884: the Faktur Pajak transaction-code picker
   * needs to show 06/07/08/10 as visible-but-unreachable options, never a
   * bespoke `<select>` — see `docs/design-system/CLAUDE.md`).
   */
  disabled?: boolean;
  /**
   * Group heading. Consecutive options sharing the same `group` render under
   * one heading inside a `role="group"` labelled by it; options without a
   * `group` render ungrouped, exactly as before. The heading is not an option:
   * it never takes the active index, so keyboard navigation and typeahead are
   * unchanged. Order the options so each group is contiguous.
   */
  group?: string;
}

/**
 * The first enabled option at or after `from`, stepping by `step` (`1` or
 * `-1`). Returns `-1` when no enabled option exists in that direction.
 */
function findEnabledIndex<V extends string>(
  options: ReadonlyArray<StyledSelectOption<V>>,
  from: number,
  step: 1 | -1,
): number {
  for (let i = from; i >= 0 && i < options.length; i += step) {
    if (options[i].disabled !== true) return i;
  }
  return -1;
}

/** Vertical gap between the trigger and the panel. */
const PANEL_GAP = 4;

/** Keeps the panel clear of the viewport edges. */
const VIEWPORT_MARGIN = 8;

/**
 * Smallest panel worth opening downwards. With less room than this below the
 * trigger, the panel flips above it instead of being squeezed to a sliver.
 */
const MIN_PANEL_HEIGHT = 120;

/**
 * Fixed-position box for the portaled panel. Exactly one of `top`/`bottom` is
 * set: `bottom` when the panel opens upwards, so it grows away from the
 * trigger without needing its rendered height measured first.
 */
interface PanelPosition {
  top?: number;
  bottom?: number;
  left: number;
  width: number;
  maxHeight: number;
}

export interface StyledSelectProps<V extends string = string> {
  value: V;
  onChange: (value: V) => void;
  options: ReadonlyArray<StyledSelectOption<V>>;
  /** Shown when `value` does not match any option (e.g. empty initial state). */
  placeholder?: string;
  /** Optional accessible label for screen readers. */
  ariaLabel?: string;
  /**
   * DOM id applied to the trigger button. Lets a surrounding `<label
   * htmlFor>` (or a `FormField`'s `htmlFor`) actually point at the
   * interactive element — this component has no native `<select>` to carry
   * an id otherwise.
   */
  id?: string;
  /**
   * Id(s) of the element(s) describing this field (e.g. help text or an
   * inline error message), applied to the trigger as `aria-describedby`.
   * Must be passed explicitly by the call site: a wrapping `Controller`
   * silently discards any `aria-*` props a parent like `FormField` tries to
   * inject via `React.cloneElement`, since `Controller`'s `render` prop
   * receives only the fields it defines itself.
   */
  ariaDescribedBy?: string;
  /** Marks the trigger `aria-invalid` when the field has a validation error. */
  ariaInvalid?: boolean;
  /** Tailwind classes applied to the trigger button. */
  triggerClassName?: string;
  /** Tailwind classes applied to the dropdown panel. */
  menuClassName?: string;
  /** Minimum panel width in px; defaults to the trigger width. */
  minMenuWidth?: number;
  /** Render prop for the trigger label; defaults to the matched option label or placeholder. */
  renderValue?: (selected: StyledSelectOption<V> | undefined) => ReactNode;
  /** Name attribute mirrored onto a hidden input so RHF / form serialization can pick it up. */
  name?: string;
  disabled?: boolean;
  /**
   * Let a long trigger label wrap onto further lines and grow the trigger,
   * instead of truncating it with an ellipsis. For selects that sit in a
   * line-item grid, where the row height follows its tallest cell and a cut-off
   * label ("VAT (Standard 18%)") is unreadable. Off by default: a select in a
   * fixed-height toolbar or filter bar must keep its single-line height.
   *
   * Pair it with a `triggerClassName` that sets a `min-h-*` rather than a fixed
   * `h-*` — a fixed height cannot grow, so the wrapped label would be clipped.
   */
  wrapLabel?: boolean;
  /**
   * Trigger affordance icon. `'single'` (default) is the chevron-down every
   * select in a toolbar, filter bar, or form field uses. `'double'` is the
   * up/down chevron pair the comboboxes use (`AccountCombobox`,
   * `TaxRateCombobox` — lucide's `ChevronsUpDown`); pass it when this select
   * sits beside one of those in the same line-item grid, so the whole row
   * reads as one register instead of mixing two open-a-list affordances.
   */
  chevron?: 'single' | 'double';
}

function StyledSelectInner<V extends string = string>(
  {
    value,
    onChange,
    options,
    placeholder,
    ariaLabel,
    id,
    ariaDescribedBy,
    ariaInvalid,
    triggerClassName,
    menuClassName,
    minMenuWidth,
    renderValue,
    name,
    disabled = false,
    wrapLabel = false,
    chevron = 'single',
  }: StyledSelectProps<V>,
  forwardedRef: ForwardedRef<HTMLButtonElement>,
) {
  // Consumed via React Context, not props — a `FormField` wraps this
  // component's parent tree with `FormFieldContext.Provider`, and Context
  // propagates through any intermediate component (including a
  // `Controller` render prop, which discards unknown props via
  // `React.cloneElement`) without needing to be threaded through manually.
  // Mirrors `Input`/`Select`/`AutoGrowTextarea`'s identical use of this
  // context. Explicit `ariaDescribedBy`/`ariaInvalid` props (when a caller
  // renders this select outside a `FormField`) take precedence.
  const formFieldContext = useFormFieldContext();
  const resolvedAriaDescribedBy =
    ariaDescribedBy ?? formFieldContext?.describedBy;
  const resolvedAriaInvalid = ariaInvalid ?? formFieldContext?.ariaInvalid;
  const [open, set_open] = useState(false);
  const [pos, set_pos] = useState<PanelPosition>({
    top: 0,
    left: 0,
    width: 0,
    maxHeight: MIN_PANEL_HEIGHT,
  });
  const [active_index, set_active_index] = useState<number>(-1);
  const trigger_ref = useRef<HTMLButtonElement>(null);
  useImperativeHandle(
    forwardedRef,
    () => trigger_ref.current as HTMLButtonElement,
  );
  const menu_ref = useRef<HTMLDivElement>(null);
  const listbox_id = useId();
  const option_id_prefix = useId();
  const group_id_prefix = useId();

  const selected = options.find((o) => o.value === value);

  /**
   * Pins the panel inside the viewport on both axes. Without this a long list
   * (a 12-month picker, a chart of accounts) opened low on the page ran off
   * the bottom edge with its tail unreachable — the panel is portaled to
   * `document.body` at a fixed position, so the page cannot scroll to reveal
   * it. The panel now caps its height to the space available and scrolls
   * internally, flipping above the trigger when that space is too small to be
   * usable. `left` is clamped for the same reason on the horizontal axis.
   */
  const update_position = useCallback(() => {
    if (!trigger_ref.current) return;
    // The trigger stopped being rendered while the panel was open — typically
    // because a responsive container around it hit a `display: none`
    // breakpoint (rotate a tablet to portrait with CostPro's topbar select
    // open). The panel is portaled to `document.body`, so it does NOT
    // disappear with its trigger, and a hidden element measures as all-zeros,
    // which would pin the panel to the viewport's top-left corner as an
    // orphan floating above everything. Close instead.
    //
    // Deliberately NOT an all-zero `getBoundingClientRect()` test: jsdom has
    // no layout engine and reports zeros for every element, so that check
    // would slam the panel shut in every unit test rather than measuring
    // anything real.
    const trigger = trigger_ref.current;
    const hidden =
      !trigger.isConnected ||
      (typeof trigger.checkVisibility === 'function'
        ? !trigger.checkVisibility()
        : getComputedStyle(trigger).display === 'none');
    if (hidden) {
      set_open(false);
      return;
    }
    const rect = trigger.getBoundingClientRect();
    const width = Math.max(rect.width, minMenuWidth ?? rect.width);
    const left = Math.max(
      VIEWPORT_MARGIN,
      Math.min(rect.left, window.innerWidth - width - VIEWPORT_MARGIN),
    );
    const space_below =
      window.innerHeight - rect.bottom - PANEL_GAP - VIEWPORT_MARGIN;
    const space_above = rect.top - PANEL_GAP - VIEWPORT_MARGIN;
    const flip_above =
      space_below < MIN_PANEL_HEIGHT && space_above > space_below;

    set_pos(
      flip_above
        ? {
            bottom: window.innerHeight - rect.top + PANEL_GAP,
            left,
            width,
            maxHeight: Math.max(space_above, MIN_PANEL_HEIGHT),
          }
        : {
            top: rect.bottom + PANEL_GAP,
            left,
            width,
            maxHeight: Math.max(space_below, MIN_PANEL_HEIGHT),
          },
    );
  }, [minMenuWidth]);

  useEffect(() => {
    if (!open) return;
    update_position();

    function handle_click_outside(e: MouseEvent) {
      const target = e.target as Node;
      if (
        trigger_ref.current &&
        !trigger_ref.current.contains(target) &&
        menu_ref.current &&
        !menu_ref.current.contains(target)
      ) {
        set_open(false);
      }
    }

    function handle_scroll_or_resize() {
      update_position();
    }

    function handle_key(e: KeyboardEvent) {
      if (e.key === 'Escape') set_open(false);
    }

    document.addEventListener('mousedown', handle_click_outside);
    document.addEventListener('keydown', handle_key);
    window.addEventListener('scroll', handle_scroll_or_resize, true);
    window.addEventListener('resize', handle_scroll_or_resize);
    return () => {
      document.removeEventListener('mousedown', handle_click_outside);
      document.removeEventListener('keydown', handle_key);
      window.removeEventListener('scroll', handle_scroll_or_resize, true);
      window.removeEventListener('resize', handle_scroll_or_resize);
    };
  }, [open, update_position]);

  // When the listbox opens: set the initial active index.
  // Runs again if options or value change while open so the active index stays consistent.
  //
  // Deliberately does NOT move DOM focus into the portaled listbox (previously
  // `menu_ref.current?.focus()`). The listbox portals to `document.body` so it
  // escapes overflow-clipping containers, which means it lives outside the DOM
  // subtree of any ancestor Radix `Dialog`/`AlertDialog` content. Radix's modal
  // `FocusScope` (`trapped: true`) listens for `focusin` on `document` and snaps
  // focus back to the last element it saw *inside* its content container the
  // instant focus lands anywhere else -- so an imperative `.focus()` on the
  // portal here would be immediately yanked back to the trigger by the
  // surrounding dialog, silently breaking all keyboard navigation whenever this
  // component is used inside a Dialog. Keeping real DOM focus on the trigger
  // button (which never leaves the dialog's content subtree) and driving the
  // open listbox purely via `aria-activedescendant` is also the standard
  // WAI-ARIA "collapsible listbox" pattern:
  // https://www.w3.org/WAI/ARIA/apg/patterns/listbox/examples/listbox-collapsible/
  useEffect(() => {
    if (!open) return;
    const valueIdx = options.findIndex((o) => o.value === value);
    // A disabled option can never be the initial active index — fall
    // through to the first enabled option instead (or -1 if every option
    // is disabled).
    const idx =
      valueIdx >= 0 && options[valueIdx].disabled !== true
        ? valueIdx
        : findEnabledIndex(options, 0, 1);
    set_active_index(idx);
  }, [open, options, value]);

  // Attached to the trigger button (not the portaled listbox) -- see the effect
  // above for why DOM focus stays on the trigger while this listbox is open.
  function handle_trigger_keydown(e: ReactKeyboardEvent<HTMLButtonElement>) {
    if (!open) return;
    // Action keys: handle immediately and return.
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (
        active_index >= 0 &&
        active_index < options.length &&
        options[active_index].disabled !== true
      ) {
        onChange(options[active_index].value);
        set_open(false);
        trigger_ref.current?.focus();
      }
      return;
    }
    if (e.key === 'Escape') {
      set_open(false);
      trigger_ref.current?.focus();
      return;
    }
    if (e.key === 'Tab') {
      set_open(false);
      return;
    }

    // Navigation keys: compute target index, then move and scroll. Every
    // branch skips disabled options — `findEnabledIndex` walks past them in
    // the direction of travel, and a branch that finds nothing further in
    // that direction leaves `active_index` unchanged (never lands on a
    // disabled option, never throws).
    let new_idx: number;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const start =
        active_index < 0 ? 0 : Math.min(active_index + 1, options.length - 1);
      const next = findEnabledIndex(options, start, 1);
      new_idx = next >= 0 ? next : active_index;
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const start = active_index < 0 ? 0 : Math.max(active_index - 1, 0);
      const prev = findEnabledIndex(options, start, -1);
      new_idx = prev >= 0 ? prev : active_index;
    } else if (e.key === 'Home') {
      e.preventDefault();
      const first = findEnabledIndex(options, 0, 1);
      new_idx = first >= 0 ? first : active_index;
    } else if (e.key === 'End') {
      e.preventDefault();
      const last = findEnabledIndex(options, options.length - 1, -1);
      new_idx = last >= 0 ? last : active_index;
    } else if (e.key.length === 1) {
      // Cycle, don't re-match. Searching from 0 always returned the FIRST
      // option with that initial, so a list like "English" / "English
      // (Indonesia)" / "English (Canada)" left the last two unreachable by
      // keyboard — a regression against the native <select> this replaces,
      // which advances through same-initial options on each press.
      const char = e.key.toLowerCase();
      const starts_with_char = (idx: number): boolean =>
        options[idx].disabled !== true &&
        options[idx].label.toLowerCase().startsWith(char);
      const from = active_index < 0 ? 0 : active_index + 1;
      let match = -1;
      for (let i = 0; i < options.length; i++) {
        const idx = (from + i) % options.length;
        if (starts_with_char(idx)) {
          match = idx;
          break;
        }
      }
      if (match < 0) return;
      new_idx = match;
    } else {
      return;
    }

    if (new_idx !== active_index) {
      set_active_index(new_idx);
      // The option elements are already rendered — scroll the target into view immediately.
      // Guard the call: jsdom (used in tests) does not implement scrollIntoView.
      const el = document.getElementById(
        `${option_id_prefix}-opt-${String(new_idx)}`,
      );
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ block: 'nearest' });
      }
    }
  }

  const trigger_label = renderValue
    ? renderValue(selected)
    : (selected?.label ?? placeholder ?? '');

  // Height follows the shared control contract — `h-11` on mobile, `h-9` at
  // `sm:`+ — the same pair `DateInput`, `DatePicker`, `Select` and `Button`'s
  // `size="icon"` use, so a select lines up with whatever sits beside it in a
  // control row at BOTH breakpoints. It previously pinned `h-9` at every width,
  // leaving it 8px short of its neighbours on mobile.
  //
  // `wrapLabel` swaps the fixed height for a floor: a wrapped label needs room
  // to add a line, and a fixed height would clip it. Call sites that pass their
  // own `triggerClassName` are responsible for the same swap.
  const trigger_classes =
    triggerClassName ??
    `nv-input flex ${wrapLabel ? 'min-h-11 sm:min-h-9' : 'h-11 sm:h-9'} w-full cursor-pointer items-center justify-between gap-2 disabled:cursor-not-allowed disabled:opacity-50`;

  const menu_classes =
    menuClassName ??
    'bg-white rounded-[12px] border border-[var(--line)] shadow-[0_12px_32px_-8px_rgba(15,23,42,0.14),0_4px_8px_-2px_rgba(15,23,42,0.06)] overflow-hidden';

  const render_option = (o: StyledSelectOption<V>, idx: number) => (
    <button
      key={o.value}
      id={`${option_id_prefix}-opt-${String(idx)}`}
      type="button"
      role="option"
      aria-selected={o.value === value}
      aria-disabled={o.disabled === true || undefined}
      onClick={() => {
        // A disabled option is visible but unreachable — ignored
        // on click, same as Enter/Space (`handle_trigger_keydown`)
        // and the keyboard-navigation skip logic above.
        if (o.disabled === true) return;
        onChange(o.value);
        set_open(false);
        trigger_ref.current?.focus();
      }}
      onMouseEnter={() => {
        if (o.disabled === true) return;
        set_active_index(idx);
      }}
      // Three separate signals, previously one:
      //
      // - Colour: `text-[color:var(--ink)]`, not `text-foreground`.
      //   No stylesheet in this repo registers a
      //   `--color-foreground`, so Tailwind emitted no rule at all
      //   and the label only looked right by inheriting from `body`
      //   (this listbox portals to `document.body`). Same class of
      //   silent miss as the `text-muted-foreground` one noted on
      //   the trigger above.
      // - Hover/keyboard highlight: the accent-derived menu-hover
      //   token every other dropdown surface in the product uses
      //   (entity switcher, notification bell, AccountPicker), so it
      //   follows a product's own accent instead of the flat cool
      //   grey `--surface-raised` this used to paint. Driven off
      //   `active_index` — which `onMouseEnter` now also sets — so
      //   arrow-key navigation is visible at all: the component
      //   tracked the active option for `aria-activedescendant` but
      //   never rendered it, leaving keyboard users with no cursor.
      // - Selected value: accent text + medium weight, so the
      //   current choice is identifiable in the list. A native
      //   `<select>` shows a checkmark for this; replacing it with a
      //   custom listbox that shows nothing loses real information.
      className={cn(
        'block w-full px-3 py-2 text-start text-sm text-[color:var(--ink)]',
        idx === active_index && 'bg-[var(--accent-surface-hover)]',
        o.value === value && 'font-medium text-[color:var(--accent)]',
        o.disabled === true && 'cursor-not-allowed opacity-50',
      )}
    >
      {o.label}
    </button>
  );

  return (
    <>
      <button
        ref={trigger_ref}
        id={id}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listbox_id}
        aria-activedescendant={
          open && active_index >= 0
            ? `${option_id_prefix}-opt-${String(active_index)}`
            : undefined
        }
        aria-label={ariaLabel}
        aria-describedby={resolvedAriaDescribedBy}
        aria-invalid={resolvedAriaInvalid || undefined}
        disabled={disabled}
        onClick={() => {
          if (!disabled) set_open((v) => !v);
        }}
        onKeyDown={handle_trigger_keydown}
        className={trigger_classes}
      >
        <span
          // The unselected state must read as a placeholder, so it uses the
          // same token `.nv-input::placeholder` does (--ink-faint) — the
          // trigger sits beside real inputs and comboboxes in a line-item
          // grid and has to match them. This previously said
          // `text-muted-foreground`, which no stylesheet in this repo defines
          // a `--color-muted-foreground` for: Tailwind emitted no rule at all
          // and the placeholder silently rendered at full ink strength.
          className={`text-start ${wrapLabel ? 'whitespace-normal break-words' : 'truncate'} ${selected ? '' : 'text-[var(--ink-faint)]'}`}
        >
          {trigger_label}
        </span>
        {/* Inline chevron SVG — no lucide-react dependency in shared package.
          Paths are copied from lucide's `chevron-down` / `chevrons-up-down` so
          the trigger is pixel-identical to a lucide-rendered combobox next to
          it. */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="ms-2 h-4 w-4 shrink-0 opacity-50"
          aria-hidden="true"
        >
          {chevron === 'double' ? (
            <>
              <path d="m7 15 5 5 5-5" />
              <path d="m7 9 5-5 5 5" />
            </>
          ) : (
            <path d="m6 9 6 6 6-6" />
          )}
        </svg>
      </button>
      {name ? <input type="hidden" name={name} value={value} /> : null}
      {open &&
        createPortal(
          <div
            ref={menu_ref}
            id={listbox_id}
            role="listbox"
            className={menu_classes}
            style={{
              position: 'fixed',
              top: pos.top,
              bottom: pos.bottom,
              left: pos.left,
              width: pos.width,
              maxHeight: pos.maxHeight,
              // The default `menuClassName` sets `overflow-hidden` to clip the
              // rounded corners; this re-opens the vertical axis so a capped
              // panel scrolls its own options instead of hiding them.
              overflowY: 'auto',
              zIndex: 9999,
              outline: 'none',
              // Explicitly opt back into pointer events. This listbox portals to
              // `document.body` (see file header), which puts it outside the DOM
              // subtree of any ancestor Radix `Dialog`/`AlertDialog`. Radix's modal
              // lockdown sets `document.body.style.pointerEvents = 'none'` while
              // open and restores `pointer-events: auto` only on its own Content
              // node -- any other element appended to `body`, including this
              // portal, inherits `none` and becomes inert to clicks. Setting
              // `auto` here directly (which cascades to the option buttons below
              // via normal CSS inheritance) makes this listbox clickable
              // regardless of which modal lockdown mechanism, if any, is active
              // on an ancestor. Found via live browser testing inside the
              // Request Leave dialog -- `elementFromPoint` resolved to
              // `document.body`, not the rendered option button.
              pointerEvents: 'auto',
            }}
          >
            {contiguousRuns(options, (o) => o.group).map((run, run_index) => {
              const buttons = run.items.map(({ item: o, index: idx }) =>
                render_option(o, idx),
              );
              if (run.key === null) return buttons;
              const heading_id = `${group_id_prefix}-${String(run_index)}`;
              return (
                <div
                  key={`${group_id_prefix}-${String(run_index)}`}
                  role="group"
                  aria-labelledby={heading_id}
                >
                  <div
                    id={heading_id}
                    className="px-3 pb-1 pt-2 text-xs font-medium text-[color:var(--ink-muted)]"
                  >
                    {run.key}
                  </div>
                  {buttons}
                </div>
              );
            })}
          </div>,
          document.body,
        )}
    </>
  );
}

/**
 * `forwardRef` wrapped around a generic inner function needs this cast —
 * `forwardRef`'s own type signature is not generic, so without it callers
 * lose the `<V extends string>` narrowing and widen to plain `string`. The
 * ref resolves to the trigger `<button>`, so `form.setFocus(name)` (React
 * Hook Form) can focus this control like any native input (#26105 fix round
 * 3 — `setFocus` was a no-op for every `<StyledSelect>`-backed field because
 * it never forwarded a ref at all).
 */
export const StyledSelect = forwardRef(StyledSelectInner) as <
  V extends string = string,
>(
  props: StyledSelectProps<V> & { ref?: ForwardedRef<HTMLButtonElement> },
) => ReturnType<typeof StyledSelectInner>;
